// Traspasos abiertos: tabla con Recibir y Cancelar (retail.inventory.update), confirmación e Idempotency-Key.
import { useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ConfirmDialog, ErrorState, Modal, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { useIdempotentAttempt } from '@/hooks/useIdempotentAttempt';
import type { TransferResponse } from '@/api/services/retail-catalog/backoffice';
import {
    useOpenTransfers,
    useRefreshInventory,
    useSettleTransfer,
    type TransferDecision,
} from '../../api/useInventoryMovements';
import type { StoreDirectory } from '../../lib/inventoryDetail';
import { useInventoryAbilities } from '../../lib/inventoryPermissions';
import { openTransfersOf, storeLabel } from '../../lib/inventoryMovements';
import { applyWriteError } from '../../lib/inventoryWriteErrors';
import { WriteErrorNotice } from '../catalog/FormBits';
import { DETAIL_TD, DETAIL_TH, DetailCard, DetailEmpty } from '../detail/DetailCard';
import { useIntegrationLock } from '../../../integration/components/IntegrationLock';

const SHOWN_PIECES = 3;

function piecesText(identifiers: readonly string[]): string {
    if (identifiers.length === 0) return '—';
    const shown = identifiers.slice(0, SHOWN_PIECES).join(', ');
    const rest = identifiers.length - SHOWN_PIECES;
    return rest > 0 ? `${shown} +${String(rest)}` : shown;
}

function units(qty: number): string {
    return `${String(qty)} ${qty === 1 ? 'unidad' : 'unidades'}`;
}

/** Confirmación de recibir o cancelar; la llave vive por traspaso y decisión. */
function SettleDialog({
    transfer,
    decision,
    stores,
    onClose,
}: {
    transfer: TransferResponse;
    decision: TransferDecision;
    stores: StoreDirectory;
    onClose: () => void;
}) {
    const settle = useSettleTransfer();
    const refresh = useRefreshInventory();
    const attempt = useIdempotentAttempt(`transfer-${decision}:${transfer.transferId}`);
    const [error, setError] = useState<string | null>(null);
    const [stale, setStale] = useState(false);
    const receive = decision === 'receive';
    const payload = { transferId: transfer.transferId, decision };
    const confirm = async () => {
        setError(null);
        try {
            await attempt.run(payload, (key) =>
                settle.mutateAsync({ transferId: transfer.transferId, decision, key }),
            );
            toast.success(receive ? 'Traspaso recibido' : 'Traspaso cancelado', {
                description: transfer.sku,
            });
            onClose();
        } catch (failure) {
            const mapped = applyWriteError(failure, attempt, payload);
            setError(mapped.message);
            if (mapped.action === 'RELOAD') {
                setStale(true);
                void refresh();
            }
        }
    };
    const destination = storeLabel(stores, receive ? transfer.toStoreId : transfer.fromStoreId);
    return (
        <ConfirmDialog
            open
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            title={receive ? '¿Recibir el traspaso?' : '¿Cancelar el traspaso?'}
            description={
                receive
                    ? `Suma ${units(transfer.qty)} de ${transfer.sku} al disponible de ${destination}.`
                    : `Regresa ${units(transfer.qty)} de ${transfer.sku} al disponible de ${destination}.`
            }
            confirmLabel={receive ? 'Recibir' : 'Cancelar traspaso'}
            cancelLabel="Volver"
            destructive={!receive}
            loading={settle.isPending}
            confirmDisabled={stale}
            onConfirm={() => {
                void confirm();
            }}
        >
            <WriteErrorNotice message={error} />
        </ConfirmDialog>
    );
}

export function OpenTransfersTable({
    transfers,
    stores,
    showSku,
}: {
    transfers: readonly TransferResponse[];
    stores: StoreDirectory;
    showSku: boolean;
}) {
    const { locked } = useIntegrationLock();
    const canMoveInventory = useInventoryAbilities().canMoveInventory && !locked;
    const [pending, setPending] = useState<{
        transfer: TransferResponse;
        decision: TransferDecision;
    } | null>(null);
    const headers = [
        'Fecha',
        ...(showSku ? ['SKU'] : []),
        'Origen',
        'Destino',
        'Cantidad',
        'Piezas',
        ...(canMoveInventory ? [''] : []),
    ];
    return (
        <>
            <table className="w-full border-collapse text-[11px]" aria-label="Traspasos abiertos">
                <thead>
                    <tr>
                        {headers.map((h, i) => (
                            <th
                                key={h || `actions-${String(i)}`}
                                className={cn(DETAIL_TH, h === 'Cantidad' && 'text-right')}
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {transfers.map((t) => (
                        <tr key={t.transferId}>
                            <td className={cn(DETAIL_TD, 'tabular-nums')}>
                                {formatDateMx(t.createdAt)}
                            </td>
                            {showSku ? (
                                <td className={cn(DETAIL_TD, 'font-mono')}>{t.sku}</td>
                            ) : null}
                            <td className={DETAIL_TD}>{storeLabel(stores, t.fromStoreId)}</td>
                            <td className={DETAIL_TD}>{storeLabel(stores, t.toStoreId)}</td>
                            <td className={cn(DETAIL_TD, 'text-right font-semibold tabular-nums')}>
                                {t.qty}
                            </td>
                            <td
                                className={cn(DETAIL_TD, 'font-mono text-[10.5px]')}
                                title={t.identifiers.join(', ') || undefined}
                            >
                                {piecesText(t.identifiers)}
                            </td>
                            {canMoveInventory ? (
                                <td className={cn(DETAIL_TD, 'whitespace-nowrap text-right')}>
                                    <Button
                                        size="sm"
                                        className="mr-1 h-6 px-2 text-[11px]"
                                        aria-label={`Recibir traspaso de ${t.sku}`}
                                        onClick={() => {
                                            setPending({ transfer: t, decision: 'receive' });
                                        }}
                                    >
                                        Recibir
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-6 px-2 text-[11px]"
                                        aria-label={`Cancelar traspaso de ${t.sku}`}
                                        onClick={() => {
                                            setPending({ transfer: t, decision: 'cancel' });
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                </td>
                            ) : null}
                        </tr>
                    ))}
                </tbody>
            </table>
            {pending ? (
                <SettleDialog
                    key={`${pending.decision}:${pending.transfer.transferId}`}
                    transfer={pending.transfer}
                    decision={pending.decision}
                    stores={stores}
                    onClose={() => {
                        setPending(null);
                    }}
                />
            ) : null}
        </>
    );
}

function OpenTransfersBody({
    query,
    transfers,
    stores,
    showSku,
    emptyText,
}: {
    query: ReturnType<typeof useOpenTransfers>;
    transfers: readonly TransferResponse[];
    stores: StoreDirectory;
    showSku: boolean;
    emptyText: string;
}) {
    if (query.isPending) {
        return (
            <div className="p-3">
                <Skeleton lines={2} />
            </div>
        );
    }
    if (query.error) {
        return (
            <ErrorState
                className="m-3"
                title="No se pudieron leer los traspasos abiertos"
                description={query.error instanceof Error ? query.error.message : undefined}
                onRetry={() => {
                    void query.refetch();
                }}
            />
        );
    }
    if (transfers.length === 0) return <DetailEmpty>{emptyText}</DetailEmpty>;
    return <OpenTransfersTable transfers={transfers} stores={stores} showSku={showSku} />;
}

/** Traspasos abiertos de un SKU, en su ficha. Sin `retail.inventory.lens.read` no se muestra. */
export function SkuOpenTransfersCard({ sku, stores }: { sku: string; stores: StoreDirectory }) {
    const { canReadTransfers } = useInventoryAbilities();
    const query = useOpenTransfers(canReadTransfers);
    if (!canReadTransfers) return null;
    const transfers = openTransfersOf(query.data?.items ?? [], sku);
    return (
        <DetailCard
            title={`Traspasos abiertos · SKU ${sku}`}
            aside={query.data ? `${String(transfers.length)} en tránsito` : undefined}
            flush
        >
            <OpenTransfersBody
                query={query}
                transfers={transfers}
                stores={stores}
                showSku={false}
                emptyText="No hay traspasos abiertos de este SKU."
            />
        </DetailCard>
    );
}

/** Botón de la barra de la lista con todos los traspasos abiertos de la cadena. */
export function OpenTransfersButton({ stores }: { stores: StoreDirectory }) {
    const { canReadTransfers } = useInventoryAbilities();
    const query = useOpenTransfers(canReadTransfers);
    const [open, setOpen] = useState(false);
    if (!canReadTransfers) return null;
    const transfers = openTransfersOf(query.data?.items ?? []);
    return (
        <>
            <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 px-2 text-[11px]"
                leftIcon={<ArrowLeftRight className="size-3" aria-hidden />}
                onClick={() => {
                    setOpen(true);
                }}
            >
                {query.data
                    ? `Traspasos abiertos (${String(transfers.length)})`
                    : 'Traspasos abiertos'}
            </Button>
            <Modal
                isOpen={open}
                onClose={() => {
                    setOpen(false);
                }}
                title="Traspasos abiertos"
                description="En tránsito: no se venden en ninguna tienda hasta que se reciban o se cancelen."
                size="xl"
            >
                <div className="max-h-[60vh] overflow-y-auto rounded-md border border-border">
                    <OpenTransfersBody
                        query={query}
                        transfers={transfers}
                        stores={stores}
                        showSku
                        emptyText="No hay traspasos abiertos en tu cadena."
                    />
                </div>
            </Modal>
        </>
    );
}
